import { useAppScrollRef } from '@doltech/core/lib/appScroll/withAppScroll';
import { fromScreen } from '@doltech/core/lib/responsive/responsive.util';
import { components } from '@doltech/pageManagement/dto';
import { InternalPageLink } from '@doltech/routeDefinition';
import { compose } from '@doltech/utils/lib/compose';
import { SpaceProps } from 'antd';
import dynamic from 'next/dynamic';
import * as React from 'react';
import styled from 'styled-components';
import { space } from 'styled-system';
import { shouldRenderOnIdle } from '../../hocs/withShouldRenderOnIdle';
import { shouldRenderOnScreen } from '../../hocs/withShouldRenderOnScreen';
import { withSuspense } from '../../hocs/withSuspense';
import { Breadcrumb } from '../Breadcrumb';
import { Separator } from '../Layout/Landing/Content/Separator';
import { ContentItemProps } from '../Layout/Landing/Content/TableOfContent/TableOfContentType';
import { LandingLayout, LandingLayoutContent } from '../Layout/Landing/LandingLayout';
import { colorsV2 } from '../colors-v2';
import { BlogCommonAuthor } from './BlogCommonAuthor';
import { BlogCover, BlogCoverProps } from './BlogCover';
import { BlogHeader, BlogHeaderProps } from './BlogHeader';

const BottomNavigationPlaceholder = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  min-height: 53px;
  background-color: #fff;
  border-top: 1px solid #e9e9ec;
  z-index: 10;

  ${fromScreen(1280)} {
    display: none;
  }
`;

const BlogCoverEditor = withSuspense(
  dynamic(() => import('./BlogCoverEditor').then((mod) => mod.BlogCoverEditor), { ssr: true })
);

const BlogTableOfContent = dynamic(
  () => import('./BlogTableOfContent').then((mod) => mod.BlogTableOfContent),
  { ssr: true }
);

const MobileInteractionBottomNavigation = compose(shouldRenderOnScreen({ toScreen: 1279 }))(
  dynamic<any>(
    () =>
      import('../Layout/Landing/BottomNavigation/InteractionBottomNavigationBase').then(
        (mod) => mod.InteractionBottomNavigation
      ),
    {
      ssr: false,
      loading: () => <BottomNavigationPlaceholder />,
    }
  )
);

const BlogPromotionSidebar = compose(
  shouldRenderOnScreen({ fromScreen: 1280 }),
  shouldRenderOnIdle()
)(
  dynamic<any>(
    () => import('./client-side/BlogPromotionSidebar').then((mod) => mod.BlogPromotionSidebar),
    {
      ssr: false,
    }
  )
);

const Main = styled<any>(LandingLayout).withConfig({
  componentId: 'LandingBlogDetailLayout_Main',
} as any)`
  ${space};
  padding-top: 16px;

  #landing-layout {
    background-color: ${colorsV2.white100};
  }

  h2,
  h3 {
    font-family: Inter, sans-serif;
  }

  b {
    font-weight: 700;
  }

  strong {
    font-weight: 900;
  }

  .blog-content {
    a {
      text-decoration: underline;
    }
  }

  .block-spacing {
    margin-bottom: 32px;
  }

  .blog-author-footer {
    margin-top: 64px;
    margin-bottom: 32px;
  }

  .responsive-blog-section {
    width: 100vw;
    padding: 0 16px;
  }

  .header-editor {
    font-family: noto-serif, serif;
    margin-bottom: 16px;
    font-size: 16px;
    line-height: 160%;
    font-weight: 400;
    color: ${colorsV2.black80};

    h1 {
      font-size: 32px;
      line-height: 130%;
      font-weight: 400;
      color: ${colorsV2.black100};
    }
  }

  .author-footer-container {
    margin-bottom: 64px;
  }

  ${fromScreen(458)} {
    .responsive-blog-section {
      max-width: 425px;
      margin: 0 auto;
      padding: 0;
    }
  }

  ${fromScreen(776)} {
    .responsive-blog-section {
      max-width: 712px;
      margin: 0 auto;
      padding: 0;
    }

    .header-editor {
      font-size: 20px;

      h1 {
        font-size: 42px;
      }
    }
  }
`;

export type SupportedSocialAction =
  | 'facebook'
  | 'clap'
  | 'bookmark'
  | 'do-test'
  | 'do-test-k12'
  | 'copy-link'
  | 'back'
  | 'share';

export interface LandingBlogDetailLayoutProps extends SpaceProps {
  header: Omit<BlogHeaderProps, 'socials' | 'headerEditor'>;
  cover?: BlogCoverProps;
  coverEditor?: BlogCoverProps;
  socials: any;
  children: any;

  tableOfContent?: ContentItemProps[];
  tableOfContentEditor?: any[];
  title?: string;

  layoutTopBar: React.ReactNode;
  seoData?: components['schemas']['PageResponseDTOObject'];
  contentFooter?: React.ReactNode;
  internalPageLink?: InternalPageLink;
  useSideBar?: boolean;
  headerEditor?: React.ReactNode;
  customSticky?: React.ReactNode;
}

const LandingBlogDetailLayoutContent = (props: LandingBlogDetailLayoutProps) => {
  const {
    header,
    headerEditor = undefined,
    cover = undefined,
    coverEditor = undefined,
    socials,
    children,
    layoutTopBar,
    tableOfContent = [],
    tableOfContentEditor = [],
    title = undefined,
    seoData,
    contentFooter,
    useSideBar = true,
    className,
  } = props;
  const { appScrollRef } = useAppScrollRef();
  const SocialButton = socials;

  return (
    <Main
      className={className}
      paddingTop={useSideBar ? '24px' : 0}
      id="blog-layout"
      isTopBarFixed={false}
      defaultSidebarOpen={false}
    >
      {layoutTopBar}
      <LandingLayoutContent.SectionMiddle>
        <div className="responsive-blog-section">
          <BlogHeader
            breadcrumb={<Breadcrumb routes={header?.routes} seoData={seoData} />}
            headerEditor={headerEditor}
            socials={
              <SocialButton
                align="horizontal"
                actions={['copy-link', 'share', 'clap']}
                seoData={seoData}
              />
            }
            seoData={seoData}
            {...header}
          />
          <div id="header-divider" className="horizontal-line" />
          {cover && <BlogCover {...cover} />}
          {coverEditor && <BlogCoverEditor {...coverEditor} />}
        </div>

        <BlogPromotionSidebar />

        <div className="responsive-blog-section">
          <BlogTableOfContent
            stickyMode="ALWAYS_SHOW"
            items={tableOfContent}
            editorItems={tableOfContentEditor}
            tocTitle={(seoData?.dol as any)?.tocTitle || seoData?.dol?.title || title}
            stickyId="blog-table-of-content"
            scrollContainerElm={appScrollRef.current}
            stickOnElm="#blog-cover"
            hideWhenIntersectWithElms={[
              '#footer-author',
              '#content-footer',
              '#compact-footer',
              '.footer-with-register',
              '.common-footer',
            ]}
            offsetY={144}
            stickOnElmPosition={{
              align: 'left',
              gapInPx: 32,
            }}
          />
        </div>

        <Separator variant="three-dot-horizontal" my="32px" />
        {children}
        <div className="break" />
        <div className="responsive-container author-footer-container">
          <div className="responsive-blog-section">
            <Separator my="24px" variant="horizontal" height="1px" bg="gray20" />
            <BlogCommonAuthor
              socials={
                <SocialButton
                  align="horizontal"
                  actions={['copy-link', 'share', 'clap']}
                  showText={false}
                  seoData={seoData}
                />
              }
              readTimeInMinutes={header.readTimeInMinutes}
              publishedDate={seoData?.lastModifiedAt || header.publishedDate}
              author={header.author}
            />
          </div>
        </div>

        <div className="break" />
        {contentFooter}
      </LandingLayoutContent.SectionMiddle>
      <MobileInteractionBottomNavigation
        layout="responsive"
        pageId={seoData?.id}
        pageUrl={seoData?.url}
        scrollContainerElm={appScrollRef.current}
        tocTitle={(seoData?.dol as any)?.tocTitle || seoData?.dol?.title || title}
        tocItems={tableOfContent}
        tocEditorItems={tableOfContentEditor}
      />
    </Main>
  );
};

export const LandingBlogDetailLayout = LandingBlogDetailLayoutContent;
